import React, { useEffect, useState, useMemo, useCallback } from "react";
import { adminApi, useAuth } from "./AuthContext";
import { toast } from "sonner";
import { LOGO_URL } from "../content";
import {
  LogOut, Search, Download, RefreshCw, Trash2, Users,
  TrendingUp, Calendar, CheckCircle2, MapPin, Phone, Mail,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_COLORS = {
  new: "bg-amber-600/20 text-amber-400 border-amber-600/30",
  contacted: "bg-blue-600/20 text-blue-400 border-blue-600/30",
  qualified: "bg-purple-600/20 text-purple-400 border-purple-600/30",
  won: "bg-emerald-600/20 text-emerald-400 border-emerald-600/30",
  lost: "bg-stone-700/40 text-stone-400 border-stone-600/40",
};
const PIE_COLORS = ["#D97706", "#3B82F6", "#A855F7", "#10B981", "#78716C"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/enquiries", {
          params: { q: query || undefined, status: statusFilter },
        }),
      ]);
      setStats(statsRes.data);
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
    } catch (err) {
      toast.error("Failed to load data", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [query, statusFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll, refreshKey]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/enquiries/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success("Status updated");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error("Update failed", { description: err?.response?.data?.detail || err.message });
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await adminApi.delete(`/admin/enquiries/${id}`);
      toast.success("Deleted");
      setRefreshKey((k) => k + 1);
      setSelected(null);
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err.message });
    }
  };

  const exportCsv = async () => {
    try {
      const res = await adminApi.get("/admin/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hari-ram-purwar-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Export failed", { description: err?.response?.data?.detail || err.message });
    }
  };

  const kpis = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Total Enquiries", value: stats.total, icon: <Users size={18} />, color: "text-amber-500" },
      { label: "New", value: stats.new, icon: <CheckCircle2 size={18} />, color: "text-amber-400" },
      { label: "This Week", value: stats.this_week, icon: <Calendar size={18} />, color: "text-blue-400" },
      { label: "This Month", value: stats.this_month, icon: <TrendingUp size={18} />, color: "text-emerald-400" },
    ];
  }, [stats]);

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-full ring-2 ring-amber-600/40 object-cover" />
            <div className="leading-tight">
              <div className="font-serif text-lg text-stone-50">Partner Console</div>
              <div className="text-amber-500 text-[10px] uppercase tracking-[0.25em]">Hari Ram Purwar · Since 1890</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-stone-400 text-xs">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              {admin?.email}
            </div>
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              data-testid="admin-refresh-btn"
              className="p-2 text-stone-300 hover:text-amber-500 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-stone-300 hover:text-amber-500 border border-stone-700 hover:border-amber-600 rounded-full px-4 py-2 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-kpis">
          {kpis.map((k) => (
            <div key={k.label} className="heritage-card p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-stone-500 text-[10px] uppercase tracking-[0.25em]">{k.label}</span>
                <span className={k.color}>{k.icon}</span>
              </div>
              <div className="font-serif text-4xl text-stone-50">{stats ? k.value : "—"}</div>
            </div>
          ))}
        </section>

        {/* Charts */}
        <section className="grid lg:grid-cols-3 gap-5">
          <div className="heritage-card p-6 lg:col-span-2">
            <h3 className="text-stone-300 text-xs uppercase tracking-[0.25em] mb-4">Enquiries — last 14 days</h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={stats?.trend_14d || []}>
                  <CartesianGrid stroke="#292524" strokeDasharray="3 3" />
                  <XAxis dataKey="day" stroke="#78716c" fontSize={11} />
                  <YAxis stroke="#78716c" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "#D97706", strokeOpacity: 0.3 }} />
                  <Line type="monotone" dataKey="count" stroke="#D97706" strokeWidth={2.5} dot={{ r: 3, fill: "#D97706" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="heritage-card p-6">
            <h3 className="text-stone-300 text-xs uppercase tracking-[0.25em] mb-4">By Status</h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={(stats?.by_status || []).filter((s) => s.count > 0)}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={3}
                  >
                    {(stats?.by_status || []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#0c0a09" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#a8a29e" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="grid lg:grid-cols-2 gap-5">
          <div className="heritage-card p-6">
            <h3 className="text-stone-300 text-xs uppercase tracking-[0.25em] mb-4">By Investment Capacity</h3>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={stats?.by_capacity || []} margin={{ left: -16, right: 8 }}>
                  <CartesianGrid stroke="#292524" strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="#78716c" fontSize={10} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis stroke="#78716c" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(217,119,6,0.08)" }} />
                  <Bar dataKey="count" fill="#D97706" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="heritage-card p-6">
            <h3 className="text-stone-300 text-xs uppercase tracking-[0.25em] mb-4">Top States</h3>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={stats?.by_state || []} margin={{ left: -16, right: 8 }}>
                  <CartesianGrid stroke="#292524" strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="#78716c" fontSize={10} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis stroke="#78716c" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(217,119,6,0.08)" }} />
                  <Bar dataKey="count" fill="#991b1b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Filters + Export */}
        <section className="heritage-card p-6" data-testid="admin-enquiries-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h3 className="font-serif text-2xl text-stone-50">Enquiries</h3>
              <p className="text-stone-500 text-sm">{total} total · filtered by your search</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
                <Input
                  data-testid="admin-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, email, mobile, city…"
                  className="pl-9 bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-10 w-72"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="bg-stone-950 border-stone-700 text-stone-100 h-10 w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 text-stone-100">
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={exportCsv}
                data-testid="admin-export-csv"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-stone-950 font-medium uppercase tracking-wider text-xs px-5 h-10 rounded-full btn-primary-glow"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto -mx-2">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-stone-500 text-[10px] uppercase tracking-[0.2em]">
                  <th className="text-left py-3 px-2">Name</th>
                  <th className="text-left py-3 px-2 hidden md:table-cell">Contact</th>
                  <th className="text-left py-3 px-2 hidden lg:table-cell">Location</th>
                  <th className="text-left py-3 px-2 hidden xl:table-cell">Capacity</th>
                  <th className="text-left py-3 px-2">Status</th>
                  <th className="text-left py-3 px-2 hidden md:table-cell">Date</th>
                  <th className="text-right py-3 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center py-12 text-stone-500">No enquiries match your filters.</td></tr>
                )}
                {items.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`admin-row-${it.id}`}
                    className="border-t border-stone-800 hover:bg-stone-900/40 transition-colors cursor-pointer"
                    onClick={() => setSelected(it)}
                  >
                    <td className="py-4 px-2">
                      <div className="font-medium text-stone-100">{it.full_name}</div>
                      <div className="text-stone-500 text-xs md:hidden">{it.mobile}</div>
                    </td>
                    <td className="py-4 px-2 hidden md:table-cell">
                      <div className="text-stone-200">{it.mobile}</div>
                      <div className="text-stone-500 text-xs">{it.email}</div>
                    </td>
                    <td className="py-4 px-2 hidden lg:table-cell text-stone-300">{it.city}, {it.state}</td>
                    <td className="py-4 px-2 hidden xl:table-cell text-stone-300">{it.investment_capacity}</td>
                    <td className="py-4 px-2" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={it.status}
                        onChange={(e) => updateStatus(it.id, e.target.value)}
                        data-testid={`admin-status-${it.id}`}
                        className={`text-xs uppercase tracking-wider rounded-full px-3 py-1.5 border outline-none cursor-pointer ${STATUS_COLORS[it.status] || STATUS_COLORS.new}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s} className="bg-stone-900 text-stone-100">{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-4 px-2 hidden md:table-cell text-stone-500 text-xs whitespace-nowrap">
                      {new Date(it.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="py-4 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => deleteOne(it.id)}
                        data-testid={`admin-delete-${it.id}`}
                        className="text-stone-500 hover:text-red-400 p-1"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer
          enquiry={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(s) => updateStatus(selected.id, s)}
          onDelete={() => deleteOne(selected.id)}
        />
      )}
    </div>
  );
}

const tooltipStyle = {
  background: "#1c1917",
  border: "1px solid #44403c",
  borderRadius: 8,
  fontSize: 12,
  color: "#fafaf9",
};

function DetailDrawer({ enquiry, onClose, onStatusChange, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="enquiry-detail-drawer">
      <div className="absolute inset-0 bg-stone-950/80 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative w-full max-w-md bg-stone-950 border-l border-stone-800 overflow-y-auto">
        <div className="p-6 border-b border-stone-800 flex items-start justify-between gap-3 sticky top-0 bg-stone-950/95 backdrop-blur z-10">
          <div>
            <h3 className="font-serif text-2xl text-stone-50">{enquiry.full_name}</h3>
            <p className="text-stone-500 text-xs uppercase tracking-wider mt-1">Enquiry detail</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-100 text-2xl leading-none" data-testid="detail-close">&times;</button>
        </div>
        <div className="p-6 space-y-5">
          <Detail icon={<Phone size={14} />} label="Mobile" value={<a href={`tel:${enquiry.mobile}`} className="text-amber-500 hover:underline">+91 {enquiry.mobile}</a>} />
          <Detail icon={<Mail size={14} />} label="Email" value={<a href={`mailto:${enquiry.email}`} className="text-amber-500 hover:underline break-all">{enquiry.email}</a>} />
          <Detail icon={<MapPin size={14} />} label="Location" value={`${enquiry.city}, ${enquiry.state}`} />
          <Detail icon={<TrendingUp size={14} />} label="Investment Capacity" value={enquiry.investment_capacity} />
          <Detail icon={<Calendar size={14} />} label="Submitted" value={new Date(enquiry.created_at).toLocaleString("en-IN")} />

          <div>
            <div className="text-stone-500 text-[10px] uppercase tracking-[0.25em] mb-2">Business Profile</div>
            <div className="text-stone-200 text-sm leading-relaxed whitespace-pre-wrap bg-stone-900 border border-stone-800 rounded-lg p-4">
              {enquiry.business_profile || <span className="text-stone-500 italic">No profile provided.</span>}
            </div>
          </div>

          <div>
            <div className="text-stone-500 text-[10px] uppercase tracking-[0.25em] mb-2">Status</div>
            <select
              value={enquiry.status}
              onChange={(e) => onStatusChange(e.target.value)}
              data-testid="detail-status-select"
              className={`w-full text-sm uppercase tracking-wider rounded-lg px-3 py-2.5 border outline-none cursor-pointer ${STATUS_COLORS[enquiry.status] || STATUS_COLORS.new}`}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s} className="bg-stone-900 text-stone-100">{s}</option>
              ))}
            </select>
          </div>

          <div className="pt-4 border-t border-stone-800 flex gap-3">
            <a
              href={`tel:${enquiry.mobile}`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-stone-950 text-sm font-medium uppercase tracking-wider px-4 py-3 rounded-full"
            >
              <Phone size={14} /> Call
            </a>
            <button
              onClick={onDelete}
              data-testid="detail-delete-btn"
              className="inline-flex items-center justify-center gap-2 border border-red-900/50 text-red-400 hover:bg-red-950/40 text-sm uppercase tracking-wider px-4 py-3 rounded-full"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-amber-500 mt-1">{icon}</span>
      <div className="flex-1">
        <div className="text-stone-500 text-[10px] uppercase tracking-[0.25em]">{label}</div>
        <div className="text-stone-100 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
